// ---------------------------------------------------------------------------
// useCanEditResource — React binding over the pure `canEditResource` rule.
// Pulls the signed-in identity from `useAuth` and the active workspace's
// creator from `useWorkspaces`, so callers only supply the workspace name and
// the resource's own creator. Shared by every resource page (agents, graders,
// eval templates, judges) to keep edit/delete gating aligned.
// ---------------------------------------------------------------------------

import { canEditResource } from '../../utils/canEditResource.js';
import { useAuth } from '../useAuth/useAuth.js';
import { useWorkspaces } from '../useWorkspaces/useWorkspaces.js';

export interface UseCanEditResourceInput {
  workspaceName: string;
  /** Resource creator (`systemProperties.createdBy`); may be unknown. */
  resourceCreatedBy?: string | null;
}

export function useCanEditResource({
  workspaceName,
  resourceCreatedBy,
}: UseCanEditResourceInput): boolean {
  const { account } = useAuth();
  const { activeWorkspace } = useWorkspaces();

  const currentUser = account?.username ?? null;
  // Only trust the workspace-creator signal when the loaded active workspace is
  // the one this resource lives in (the selector is global, not per-page).
  const workspaceCreatedBy =
    activeWorkspace !== null && activeWorkspace.name === workspaceName
      ? activeWorkspace.systemProperties?.createdBy
      : null;

  return canEditResource({
    workspaceName,
    currentUser,
    resourceCreatedBy,
    workspaceCreatedBy,
  });
}
